/* Finance Hub — installment-friendly. The three primary CTAs match how
 * GCU's payment portal works: Make Payment / Request Invoice / Financial
 * Estimate. Below them: balance summary, transactions list, helpful links. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Wallet, Receipt, TrendingUp, Calendar, ExternalLink,
  CheckCircle2, Clock, XCircle, ShieldCheck, GraduationCap,
  CreditCard, FileText, Calculator, Send, X, Check,
} from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

const STATUS_PILL = {
  paid:    { Icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { Icon: Clock,        cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  open:    { Icon: Clock,        cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  unpaid:  { Icon: Clock,        cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  expired: { Icon: XCircle,      cls: 'bg-red-50 text-red-700 border-red-200' },
};
const REQ_STATUS = {
  pending:    { cls: 'bg-amber-50 text-amber-700 border-amber-200',     label: 'Pending' },
  processing: { cls: 'bg-sky-50 text-sky-700 border-sky-200',             label: 'Processing' },
  sent:       { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Sent' },
  declined:   { cls: 'bg-red-50 text-red-700 border-red-200',             label: 'Declined' },
};

function fmt(n, currency = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(n) || 0);
}

export default function Finance() {
  const { api } = useAuth();
  const [data, setData] = useState({ summary: null, rows: [] });
  const [invoiceReqs, setInvoiceReqs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tierDefs, setTierDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'invoice' | 'estimate' | null

  const reload = async () => {
    const [fin, inv] = await Promise.all([
      api.get('/api/academy/my/finance').catch(() => ({ data: { summary: null, rows: [] } })),
      api.get('/api/academy/my/invoice-requests').catch(() => ({ data: { rows: [] } })),
    ]);
    setData(fin.data);
    setInvoiceReqs(inv.data.rows || []);
  };

  useEffect(() => {
    let active = true;
    Promise.all([
      reload(),
      api.get('/api/academy/programs').then(({ data }) => {
        if (!active) return;
        setPrograms(data.programs || []);
        setTierDefs(data.tiers || []);
      }).catch(() => {}),
    ]).finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line
  }, [api]);

  const s = data.summary || {};
  const cur = s.currency || 'USD';

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar />
      <main className="max-w-[1100px] mx-auto px-4 lg:px-6 py-8">
        <Link to="/academy/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-[#6E6E73] hover:text-[#c9a227] mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">Finance Hub</h1>
        <p className="text-[14px] text-[#6E6E73] mb-7">Tuition, payments, and invoices in one place.</p>

        {loading ? (
          <div className="text-[#6E6E73] text-[14px]">Loading…</div>
        ) : (
          <>
            {/* ===== Action panel — 3 primary CTAs (GCU-style) ===== */}
            <div className="rounded-2xl border border-[#E5E5E7] bg-white p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
              data-testid="finance-actions">
              <ActionButton
                icon={CreditCard}
                label="Make Payment"
                hint="Pay your installment securely."
                onClick={() => { window.location.href = '/academy/programs'; }}
                testId="finance-action-make-payment"
                primary
              />
              <ActionButton
                icon={FileText}
                label="Request Invoice"
                hint="Email me an invoice for this month."
                onClick={() => setModal('invoice')}
                testId="finance-action-request-invoice"
              />
              <ActionButton
                icon={Calculator}
                label="Financial Estimate"
                hint="See total tuition + monthly plan."
                onClick={() => setModal('estimate')}
                testId="finance-action-estimate"
              />
            </div>

            {/* Summary tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <SummaryTile
                icon={Wallet}
                eyebrow="Outstanding balance"
                value={fmt(s.outstanding_total, cur)}
                tone={(s.outstanding_total || 0) > 0 ? 'amber' : 'default'}
                testId="finance-outstanding"
              />
              <SummaryTile
                icon={TrendingUp}
                eyebrow="Total paid to date"
                value={fmt(s.paid_total, cur)}
                tone="emerald"
                testId="finance-paid"
              />
              <SummaryTile
                icon={Calendar}
                eyebrow="Last payment"
                value={s.last_paid_at ? new Date(s.last_paid_at).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}
                hint={s.transaction_count ? `${s.transaction_count} transactions on file` : 'No transactions yet'}
                testId="finance-last"
              />
            </div>

            {/* Invoice requests history */}
            {invoiceReqs.length > 0 && (
              <div className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white mb-6"
                data-testid="finance-invoice-requests">
                <div className="bg-[#0e0e10] text-white px-5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#f5a623]" />
                    <span className="text-[12px] font-bold tracking-[0.18em] uppercase">Invoice requests</span>
                  </div>
                  <span className="text-[11px] text-white/60">{invoiceReqs.length}</span>
                </div>
                <ul className="divide-y divide-[#E5E5E7]">
                  {invoiceReqs.slice(0, 5).map((r) => {
                    const meta = REQ_STATUS[r.status] || REQ_STATUS.pending;
                    return (
                      <li key={r.id} data-testid={`finance-invoice-req-${r.id}`}
                        className="px-5 py-3 flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-[13.5px] font-bold text-[#0e0e10]">{r.purpose}</div>
                          <div className="text-[11.5px] text-[#6E6E73] mt-0.5">
                            {fmt(r.amount, r.currency)} · Submitted {new Date(r.created_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                          </div>
                        </div>
                        <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                          {meta.label}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Transactions */}
            <div className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white mb-7">
              <div className="bg-[#0e0e10] text-white px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-[#f5a623]" />
                  <span className="text-[12px] font-bold tracking-[0.18em] uppercase">Recent transactions</span>
                </div>
                <Link to="/academy/invoices" className="text-[11px] uppercase tracking-wider text-white/60 hover:text-[#f5a623]">
                  View all →
                </Link>
              </div>
              {data.rows.length === 0 ? (
                <div className="p-8 text-center text-[#6E6E73]">
                  <Receipt className="w-8 h-8 text-[#c9a227] mx-auto mb-2" />
                  <div className="text-[13.5px] font-bold text-[#0e0e10]">No transactions yet</div>
                  <p className="text-[12.5px]">Your payment history will appear here after enrolment.</p>
                </div>
              ) : (
                <table className="w-full text-[13.5px]">
                  <thead className="bg-[#FAFAFA] text-[10.5px] uppercase tracking-[0.18em] text-[#6E6E73]">
                    <tr>
                      <th className="px-5 py-2.5 text-left">Date</th>
                      <th className="px-5 py-2.5 text-left">Item</th>
                      <th className="px-5 py-2.5 text-right">Amount</th>
                      <th className="px-5 py-2.5 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E7]">
                    {data.rows.slice(0, 8).map((r, i) => {
                      const ps = (r.payment_status || 'pending').toLowerCase();
                      const meta = STATUS_PILL[ps] || STATUS_PILL.pending;
                      const Icon = meta.Icon;
                      return (
                        <tr key={r.session_id || i} data-testid={`finance-tx-${i}`}>
                          <td className="px-5 py-3 text-[#6E6E73]">{(r.created_at || '').slice(0, 10)}</td>
                          <td className="px-5 py-3 font-bold text-[#0e0e10]">
                            {r.service_name || r.slug}
                            {r.tier_name && <div className="text-[11.5px] font-normal text-[#6E6E73]">{r.tier_name}</div>}
                          </td>
                          <td className="px-5 py-3 text-right font-bold text-[#0e0e10]">
                            {fmt(r.amount, (r.currency || 'usd').toUpperCase())}
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase tracking-wider border ${meta.cls}`}>
                              <Icon className="w-3 h-3" /> {ps}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Helpful links */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <HelpfulLink Icon={ShieldCheck} title="Tuition & refund policy"
                body="Read how tuition, refunds and payment plans work." href="/academy/support" />
              <HelpfulLink Icon={GraduationCap} title="Talk to your advisor"
                body="Schedule a call to map out your payment path." href="/academy/counselors" />
              <HelpfulLink Icon={Receipt} title="All invoices"
                body="View, download and reconcile every invoice." href="/academy/invoices" />
            </div>
          </>
        )}

        {modal === 'invoice' && (
          <InvoiceRequestModal
            programs={programs}
            api={api}
            onClose={() => setModal(null)}
            onCreated={() => { setModal(null); reload(); }}
          />
        )}
        {modal === 'estimate' && (
          <EstimateModal programs={programs} tierDefs={tierDefs} onClose={() => setModal(null)} />
        )}
      </main>
    </div>
  );
}

function ActionButton({ icon: Icon, label, hint, onClick, testId, primary }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`group text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5
        ${primary
          ? 'border-[#c9a227] bg-gradient-to-br from-[#fff8e7] to-white shadow-[0_10px_30px_-15px_rgba(201,162,39,0.45)]'
          : 'border-[#E5E5E7] bg-white hover:border-[#c9a227]/60 hover:shadow-[0_10px_30px_-18px_rgba(201,162,39,0.3)]'}`}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-2.5 transition-transform group-hover:rotate-6
        ${primary ? 'bg-gradient-to-br from-[#e6c35a] to-[#c9a227] text-[#0e0e10]' : 'bg-[#fff8e7] text-[#a8871a]'}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className={`text-[14px] font-extrabold ${primary ? 'text-[#0e0e10]' : 'text-[#0e0e10] group-hover:text-[#a8871a]'}`}>
        {label}
      </div>
      <div className="text-[11.5px] text-[#6E6E73] mt-1 leading-snug">{hint}</div>
    </button>
  );
}

function SummaryTile({ icon: Icon, eyebrow, value, hint, tone = 'default', testId }) {
  const toneCfg = {
    amber:   { ring: 'border-amber-200',   iconBg: 'bg-amber-50',   iconFg: 'text-amber-700' },
    emerald: { ring: 'border-emerald-200', iconBg: 'bg-emerald-50', iconFg: 'text-emerald-700' },
    default: { ring: 'border-[#E5E5E7]',   iconBg: 'bg-[#fff8e7]',  iconFg: 'text-[#a8871a]' },
  }[tone];
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-5 ${toneCfg.ring}`}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-3 ${toneCfg.iconBg} ${toneCfg.iconFg}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a] mb-1">{eyebrow}</div>
      <div className="text-[24px] font-extrabold text-[#0e0e10] tabular-nums leading-none">{value}</div>
      {hint && <div className="text-[11.5px] text-[#6E6E73] mt-2">{hint}</div>}
    </div>
  );
}

function HelpfulLink({ Icon, title, body, href }) {
  return (
    <Link to={href} className="group rounded-2xl border border-[#E5E5E7] bg-white p-5 transition-all hover:border-[#c9a227]/50 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_rgba(201,162,39,0.25)]">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 shrink-0 rounded-xl bg-[#fff8e7] text-[#a8871a] items-center justify-center">
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1">
          <div className="text-[13.5px] font-extrabold text-[#0e0e10] group-hover:text-[#a8871a] transition-colors">{title}</div>
          <p className="text-[12px] text-[#6E6E73] mt-0.5 leading-relaxed">{body}</p>
        </div>
      </div>
    </Link>
  );
}

/* ---------- Invoice request modal ---------- */
function InvoiceRequestModal({ programs, api, onClose, onCreated }) {
  const [programSlug, setProgramSlug] = useState(programs?.[0]?.slug || '');
  const [purpose, setPurpose] = useState(`${new Date().toLocaleString(undefined, { month: 'long', year: 'numeric' })} installment`);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(''); setSuccess('');
    try {
      await api.post('/api/academy/my/invoice-requests', {
        program_slug: programSlug || null,
        amount: Number(amount),
        currency,
        purpose,
        notes: notes || null,
      });
      setSuccess('Invoice request submitted. Finance will email you shortly.');
      setTimeout(onCreated, 900);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="finance-invoice-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-[#0e0e10] text-white px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#f5a623]" />
            <span className="text-[13px] font-bold tracking-[0.18em] uppercase">Request invoice</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Program</span>
            <select value={programSlug} onChange={(e) => setProgramSlug(e.target.value)}
              data-testid="finance-invoice-program"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]">
              <option value="">— general —</option>
              {(programs || []).map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Purpose</span>
            <input value={purpose} onChange={(e) => setPurpose(e.target.value)} required maxLength={200}
              data-testid="finance-invoice-purpose"
              placeholder="e.g. April 2026 installment"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
          </label>
          <div className="grid grid-cols-[2fr_1fr] gap-2">
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Amount</span>
              <input type="number" min="1" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="finance-invoice-amount"
                placeholder="125.00"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
            </label>
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Currency</span>
              <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                data-testid="finance-invoice-currency"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]">
                <option value="USD">USD</option>
                <option value="GHS">GHS</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Notes <span className="text-[#9aa0a6] font-normal lowercase">(optional)</span></span>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500}
              data-testid="finance-invoice-notes"
              placeholder="Anything Finance should know"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]" />
          </label>
          {success && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12.5px] text-emerald-800 inline-flex items-center gap-1.5"
            data-testid="finance-invoice-success"><Check className="w-3.5 h-3.5" /> {success}</div>}
          {error && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-[12.5px] text-red-700"
            data-testid="finance-invoice-error">{error}</div>}
        </div>
        <div className="px-5 py-4 bg-[#FAFAFA] flex items-center justify-end gap-2 border-t border-[#E5E5E7]">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#6E6E73] hover:text-[#0e0e10]">
            Cancel
          </button>
          <button type="submit" disabled={busy}
            data-testid="finance-invoice-submit"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10] disabled:opacity-50"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
            <Send className="w-3.5 h-3.5" /> {busy ? 'Sending…' : 'Submit request'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ---------- Estimate modal — pure client-side calculator ---------- */
function EstimateModal({ programs, tierDefs = [], onClose }) {
  const [programSlug, setProgramSlug] = useState(programs?.[0]?.slug || '');
  const [tierId, setTierId] = useState('');
  const [months, setMonths] = useState(6);

  const program = (programs || []).find((p) => p.slug === programSlug) || programs?.[0];
  const prices = program?.prices || {};
  // Tier list = official tier defs filtered to those that have a price for this program.
  const availableTiers = tierDefs.filter((t) => prices[t.id] != null);
  const activeTierId = tierId || availableTiers[0]?.id || '';
  const total = Number(prices[activeTierId] || 0);
  const monthly = months > 0 ? total / months : total;
  const cur = 'USD';

  return (
    <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="finance-estimate-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-[#0e0e10] text-white px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-[#f5a623]" />
            <span className="text-[13px] font-bold tracking-[0.18em] uppercase">Financial estimate</span>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-white/70 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Program</span>
            <select value={programSlug} onChange={(e) => { setProgramSlug(e.target.value); setTierId(''); }}
              data-testid="finance-estimate-program"
              className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]">
              {(programs || []).map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
          </label>
          {availableTiers.length > 0 && (
            <label className="block">
              <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">Tier</span>
              <select value={activeTierId} onChange={(e) => setTierId(e.target.value)}
                data-testid="finance-estimate-tier"
                className="w-full rounded-lg border border-[#E5E5E7] px-3 py-2 text-[13px] outline-none focus:border-[#c9a227]">
                {availableTiers.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} — {fmt(prices[t.id], cur)}</option>
                ))}
              </select>
            </label>
          )}
          <label className="block">
            <span className="block text-[10.5px] uppercase tracking-[0.2em] font-bold text-[#a8871a] mb-1">
              Spread over (months): <span className="text-[#0e0e10] tabular-nums">{months}</span>
            </span>
            <input type="range" min={1} max={12} step={1} value={months} onChange={(e) => setMonths(Number(e.target.value))}
              data-testid="finance-estimate-months"
              className="w-full accent-[#c9a227]" />
            <div className="flex justify-between text-[10px] text-[#9aa0a6] mt-0.5"><span>1 mo</span><span>12 mo</span></div>
          </label>

          <div className="rounded-2xl border border-[#c9a227]/30 bg-[#fff8e7] p-4 mt-4"
            data-testid="finance-estimate-result">
            <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">Estimated monthly</div>
            <div className="text-[34px] font-extrabold text-[#0e0e10] tabular-nums leading-none mt-1">
              {fmt(monthly, cur)}
            </div>
            <div className="text-[12px] text-[#6E6E73] mt-2">
              {months} payment{months === 1 ? '' : 's'} · Total {fmt(total, cur)}
            </div>
          </div>
          <p className="text-[11px] text-[#9aa0a6] leading-relaxed">
            Estimate only. Final installment plans are confirmed by your advisor and may include optional setup fees.
          </p>
        </div>
        <div className="px-5 py-4 bg-[#FAFAFA] flex items-center justify-between gap-2 border-t border-[#E5E5E7]">
          <Link to="/academy/counselors"
            className="text-[12px] font-bold uppercase tracking-wider text-[#a8871a] hover:underline">
            Talk to advisor →
          </Link>
          <button type="button" onClick={onClose}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
            style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
